import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Button} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert, assertAndContinue} from 'Asserts';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec, isStringSpec} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import TextArea from 'ui-library/bs/TextArea';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import BenchlingIcon, {isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import MarkdownContent from 'ui-library/MarkdownContent';
import {JSONObject, JSONValue} from 'util/Types';

import EnumSelector from '../EnumSelector';
import {isEditorDisabled} from '../step-config-settings/StepConfigDisableFieldFunctions';
import {
  doesConditionMatch,
  getColumnsFromDataFramesInfos,
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  EnumUISchema,
  StepConfigEditorOverride,
  StepConfigUISchema,
  ConfigShapePath,
  isShapeUISchema,
  assertTypeValidForEditorOverride,
  TaggedUnionUISchema,
  CONDITIONAL_UI_SCHEMA_KEY,
  isCheckboxFieldSettings,
  isColumnReferenceFieldSettings,
  CUSTOM_COMPONENT_KEY,
  isFieldUISchemaWithCustomComponent,
  isColumnValueFieldSettings,
  isTableReferenceFieldSettings,
  isFolderFieldSettings,
  isSchemaFilterFieldSettings,
  isArrayFieldSettings,
  isColumnMappingFieldSettings,
  isSegmentedEnumFieldSettings,
  isStringFieldSettings,
  isFieldUISchemaActionButton,
  FIELD_ORDER_UI_SCHEMA_KEY,
  ShapeUISchema,
  isMappingArrayFieldSettings,
  FieldUISchemaWithStandardComponent,
  ConfigDataFrameInfo,
  DisableFunctionParams,
  isFieldUISchemaMarkdown,
  isRangeFieldSettings,
  isFileUploadFieldSettings,
  isTaggedUnionFieldSettings,
  TaggedUnionFieldSettings,
  ArrayExpandableItemControls,
} from '../StepConfigEditorTypes';
import ArrayEditor from './ArrayEditor';
import ColorEditor from './ColorEditor';
import ColumnReferenceEditor from './ColumnReferenceEditor';
import ColumnMappingEditor from './custom-editors/ColumnMappingEditor';
import DataFrameColumnValueEditor from './DataFrameColumnValueEditor';
import DateEditor from './DateEditor';
import DateRangeEditor from './DateRangeEditor';
import EnumEditor from './EnumEditor';
import FileUploadEditor from './FileUploadEditor';
import FolderEditor from './FolderEditor';
import LocationEditor from './LocationEditor';
import QueryingMappingArrayEditor from './MappingArrayEditor';
import MultiSelectColumnReferenceEditor from './MultiSelectColumnReferenceEditor';
import MultiSelectSearchEditor from './MultiSelectSearchEditor';
import PlateDesignTemplateSearchEditor from './PlateDesignTemplateSearchEditor';
import SchemaEditor from './SchemaEditor';
import SearchEditor from './SearchEditor';
import ShapeEditor from './ShapeEditor';
import {BOOLEAN_EDITOR_OPTIONS} from './StepConfigEditorConstants';
import {VariableHeaderOrNull} from './StepConfigFieldsTemplate';
import {getMinMaxErrorMessage} from './stepConfigValidationUtils';
import TableReferenceEditor from './TableReferenceEditor';
import {TagChoiceDropdown} from './TagChoiceDropdown';
import TaggedUnionEditor from './TaggedUnionEditor';

export interface StepConfigEditorRouterProps {
  config: Record<string, JSONValue>;
  configShape: PipelineConfigJsonSpec;
  dataTestAttrs?: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONValue, path: ConfigShapePath, newValue: JSONValue) => void;
  uiSchema: StepConfigUISchema;
  disabled?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  arrayItemControls?: ArrayExpandableItemControls;
}

export interface FieldEditorProps extends Omit<StepConfigEditorRouterProps, 'uiSchema'> {
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>;
  uiSchema: FieldUISchemaWithStandardComponent;
}

export function FieldEditor({
  dataFramesInfos,
  config,
  onChangeConfig,
  configShape,
  uiSchema,
  disabled,
  dataTestAttrs = {},
  isExpandable,
  isExpanded,
}: FieldEditorProps): JSX.Element {
  const columns = getColumnsFromDataFramesInfos(dataFramesInfos);
  const shouldDisableEditor = isEditorDisabled({uiSchema, config, columns}) || disabled;
  const currentConfigShape = getConfigShapeAtConfigShapePath(uiSchema.configShapePath, configShape, config);
  if (uiSchema.editorOverride) {
    assertTypeValidForEditorOverride(uiSchema.editorOverride, currentConfigShape);
  }
  switch (currentConfigShape.kind) {
    case 'string': {
      if (uiSchema.editorOverride === StepConfigEditorOverride.STRING_LABEL) {
        return (
          <div className={css(styles.oneRowWrapper, styles.stringLabelWrapper)}>
            <VariableHeaderOrNull
              variableName={
                (maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''
              }
              isCompact={true}
            />
          </div>
        );
      }
      if (uiSchema.editorOverride === StepConfigEditorOverride.FILE_UPLOAD) {
        assert(
          isFileUploadFieldSettings(uiSchema.fieldSettings),
          'FILE_UPLOAD editorOverride requires FileUploadFieldSettings'
        );
        const fileUploadSettings = uiSchema.fieldSettings;
        return (
          <FileUploadEditor
            config={config}
            s3UrlPath={uiSchema.configShapePath}
            fileNamePath={fileUploadSettings.fileNamePath}
            accept={fileUploadSettings.accept}
            maxFileSizeBytes={fileUploadSettings.maxFileSizeBytes}
            onChangeConfig={onChangeConfig}
            disabled={shouldDisableEditor}
          />
        );
      }

      let useTextArea = false;
      if (uiSchema.hasOwnProperty('fieldSettings')) {
        assert(isStringFieldSettings(uiSchema.fieldSettings), 'Invalid field settings for string field');
        useTextArea = uiSchema.fieldSettings?.useTextArea ?? false;
      }

      if (useTextArea) {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <TextArea
                className={css(styles.textArea)}
                dataTestAttrs={{component: 'StepConfigEditor', element: 'textArea', ...dataTestAttrs}}
                value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
                onChange={(e) => {
                  onChangeConfig(config, uiSchema.configShapePath, e.target.value);
                }}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      } else {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <TextInput
              className={css(styles.primitiveEditor)}
              rootClassName={css(styles.grow)}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'textInput', ...dataTestAttrs}}
              value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
              placeholder="Text"
              onChange={(e) => {
                onChangeConfig(config, uiSchema.configShapePath, e.target.value);
              }}
              disabled={shouldDisableEditor}
            />
          </div>
        );
      }
    }
    case 'boolean':
      if (uiSchema.editorOverride === StepConfigEditorOverride.CHECKBOX) {
        assert(
          isCheckboxFieldSettings(uiSchema.fieldSettings),
          'Checkbox label is required for checkbox editor override'
        );
        const checkboxLabel = uiSchema.fieldSettings.checkboxLabel;
        let checked = maybeGetValueFromConfig(config, uiSchema.configShapePath) as boolean | false;
        if (checked == null && uiSchema.fieldSettings.defaultValue != null) {
          checked = uiSchema.fieldSettings.defaultValue;
        }
        return (
          <div className={css(styles.oneRowWrapper)}>
            <CheckboxInput
              dataTestAttrs={{component: 'StepConfigEditor', element: 'checkbox', ...dataTestAttrs}}
              checked={checked}
              onChange={(e) => {
                onChangeConfig(config, uiSchema.configShapePath, e.target.checked);
              }}
              disabled={shouldDisableEditor}
              id={`${uiSchema.configShapePath.join('.')}-${dataTestAttrs?.index ? dataTestAttrs.index : ''}`}
              label={checkboxLabel}
            />
          </div>
        );
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DropdownSelector<boolean>
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              selectedValue={maybeGetValueFromConfig(config, uiSchema.configShapePath) as boolean | null}
              onChange={(selectedValue) => {
                onChangeConfig(config, uiSchema.configShapePath, selectedValue);
              }}
              allowNull={!currentConfigShape.required}
              buttonSize={ButtonSize.SMALL}
              searchable={false}
              optionDatas={BOOLEAN_EDITOR_OPTIONS}
              disabled={shouldDisableEditor}
            />
          </div>
        </div>
      );
    case 'number':
      return (
        <div className={css(styles.oneRowWrapper)}>
          <IntermediateValueControlledNumericInput
            dataTestAttrs={{component: 'StepConfigEditor', element: 'numericInput', ...dataTestAttrs}}
            onChange={(e) => {
              onChangeConfig(
                config,
                uiSchema.configShapePath,
                e.target.numericValue ?? (e.target.value !== '' ? e.target.value : null)
              );
            }}
            value={maybeGetValueFromConfig(config, uiSchema.configShapePath) as number | null}
            placeholder="Number"
            className={css(styles.primitiveEditor)}
            isDisabled={shouldDisableEditor}
          />
        </div>
      );
    case 'integer':
      return (
        <>
          <div className={css(styles.oneRowWrapper)}>
            <IntermediateValueControlledNumericInput
              dataTestAttrs={{component: 'StepConfigEditor', element: 'numericInput', ...dataTestAttrs}}
              onChange={(e) => {
                onChangeConfig(
                  config,
                  uiSchema.configShapePath,
                  e.target.numericValue ?? (e.target.value !== '' ? e.target.value : null)
                );
              }}
              value={maybeGetValueFromConfig(config, uiSchema.configShapePath) as number | null}
              placeholder="Integer"
              className={css(styles.primitiveEditor)}
              validateInteger={true}
              min={currentConfigShape.minValue}
              max={currentConfigShape.maxValue}
              isDisabled={shouldDisableEditor}
            />
          </div>
          {getMinMaxErrorMessage(config, currentConfigShape) && (
            <FieldError message={getMinMaxErrorMessage(config, currentConfigShape)} />
          )}
        </>
      );
    case 'color':
      return (
        <div className={css(styles.oneRowWrapper, styles.grow)}>
          <ColorEditor
            value={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null) ?? ''}
            onChange={(value: string) => {
              onChangeConfig(config, uiSchema.configShapePath, value);
            }}
            disabled={shouldDisableEditor}
            className={css(styles.colorInput)}
          />
        </div>
      );
    case 'date': {
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DateEditor
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'datePicker', ...dataTestAttrs}}
              disabled={shouldDisableEditor}
            />
          </div>
        </div>
      );
    }
    case 'enum': {
      const enumUISchema = {...uiSchema, ...uiSchema.fieldSettings};

      // The FieldSettings union (EnumFieldSettings | SegmentedEnumFieldSettings | …) doesn't
      // expose disabledChoicesFn on all branches. Cast to access the optional enum-specific fields.
      const enumFieldSettings = uiSchema.fieldSettings as
        | {
            disabledChoicesFn?: (params: DisableFunctionParams) => ReadonlySet<string>;
            disabledChoicesTooltip?: string;
          }
        | undefined;
      const disabledChoices = enumFieldSettings?.disabledChoicesFn?.({config, columns});
      const disabledChoicesTooltip = enumFieldSettings?.disabledChoicesTooltip;

      if (
        uiSchema.editorOverride === StepConfigEditorOverride.ICON_SELECTOR ||
        uiSchema.editorOverride === StepConfigEditorOverride.SEGMENTED_ENUM_SELECTOR
      ) {
        assert(
          isSegmentedEnumFieldSettings(uiSchema.fieldSettings),
          'SegmentedEnumFieldSettings is required for segmented enum editor override'
        );
        return (
          <div className={css(styles.oneRowWrapper, styles.grow)}>
            <EnumSelector
              dataTestAttrs={{component: 'StepConfigEditor', element: 'iconSelector', ...dataTestAttrs}}
              selectedOption={
                maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | number | null
              }
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              fieldSettings={uiSchema.fieldSettings}
              disabled={shouldDisableEditor}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
            />
          </div>
        );
      }
      {
        let enumValue = maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | number | null;
        if (
          enumValue == null &&
          uiSchema.fieldSettings != null &&
          Object.hasOwn(uiSchema.fieldSettings, 'defaultValue') &&
          (uiSchema.fieldSettings as {defaultValue?: string | number}).defaultValue != null
        ) {
          enumValue = (uiSchema.fieldSettings as {defaultValue: string | number}).defaultValue;
        }
        return (
          <div className={css(styles.oneRowWrapper, styles.grow)}>
            <EnumEditor
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              config={enumValue}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              uiSchema={enumUISchema as EnumUISchema | undefined}
              disabled={shouldDisableEditor}
              disabledChoices={disabledChoices}
              disabledChoicesTooltip={disabledChoicesTooltip}
              buttonSize={ButtonSize.SMALL}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
            />
          </div>
        );
      }
    }
    case 'taggedUnion': {
      const mergedFieldSettings = {...uiSchema.fieldSettings} as TaggedUnionFieldSettings | undefined;
      if (isTaggedUnionFieldSettings(mergedFieldSettings)) {
        return (
          <TaggedUnionEditor
            config={config}
            configShape={currentConfigShape}
            configShapePath={uiSchema.configShapePath}
            onChangeConfig={onChangeConfig}
            fieldSettings={mergedFieldSettings}
            disabled={shouldDisableEditor}
          />
        );
      }
      // Fallback: render just the tag dropdown for tagged unions without full field settings
      const tagKey = currentConfigShape.tagKey;
      const taggedUnionUISchema = {...uiSchema, ...uiSchema.fieldSettings};
      return (
        <TagChoiceDropdown
          selectedTag={
            maybeGetValueFromConfig(config, [...uiSchema.configShapePath, tagKey]) as string | null
          }
          onChangeChoice={(choiceShape: GetPipelineConfigSpecType<'shape'>) => {
            const tagValueShape = choiceShape.fields[tagKey];
            assertAndContinue(isStringSpec(tagValueShape), 'Tag value must be a string', {
              kind: tagValueShape.kind,
            });
            // TODO(BNCH-119690): Update config to have fields that are in the new tagged union shape
            onChangeConfig(
              config,
              [...uiSchema.configShapePath, tagKey],
              // Typescript does not understand the previous assert
              (tagValueShape as Extract<PipelineConfigJsonSpec, {kind: 'string'}>).value as string
            );
          }}
          uiSchema={taggedUnionUISchema as TaggedUnionUISchema | undefined}
          configShape={currentConfigShape}
          disabled={shouldDisableEditor}
          buttonSize={ButtonSize.SMALL}
        />
      );
    }
    case 'columnReference': {
      let noColumnSelectedText;
      let allowSelectNull;
      let filterOptions;
      let renderCyclingSelector;
      if (uiSchema.hasOwnProperty('fieldSettings')) {
        assert(
          isColumnReferenceFieldSettings(uiSchema.fieldSettings),
          'columnReference fieldSettings must be type ColumnReferenceFieldSettings'
        );
        noColumnSelectedText = uiSchema.fieldSettings?.noColumnSelectedText;
        allowSelectNull = uiSchema.fieldSettings?.allowSelectNull;
        filterOptions = uiSchema.fieldSettings?.systemNameWildcardSelector
          ? {systemNameWildcardSelector: uiSchema.fieldSettings.systemNameWildcardSelector}
          : undefined;
        renderCyclingSelector = uiSchema.fieldSettings?.renderCyclingSelector ?? false;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <ColumnReferenceEditor
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              disabled={shouldDisableEditor}
              buttonSize={ButtonSize.SMALL}
              noColumnSelectedText={noColumnSelectedText}
              allowSelectNull={allowSelectNull}
              filterOptions={filterOptions}
              renderCyclingSelector={renderCyclingSelector}
              isExpandable={isExpandable}
              isExpanded={isExpanded}
            />
          </div>
        </div>
      );
    }
    case 'columnValue': {
      let noValueSelectedText;
      let renderCyclingSelector;
      if (uiSchema.fieldSettings != null) {
        assert(
          isColumnValueFieldSettings(uiSchema.fieldSettings),
          'columnValue fieldSettings must be type ColumnValueFieldSettings'
        );
        noValueSelectedText = uiSchema.fieldSettings.noValueSelectedText;
        renderCyclingSelector = uiSchema.fieldSettings?.renderCyclingSelector ?? false;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DataFrameColumnValueEditor
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              config={maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null}
              configShape={currentConfigShape}
              onChangeConfig={(newValue) => {
                onChangeConfig(config, uiSchema.configShapePath, newValue);
              }}
              disabled={shouldDisableEditor}
              buttonSize={ButtonSize.SMALL}
              noValueSelectedText={noValueSelectedText}
              renderCyclingSelector={renderCyclingSelector}
            />
          </div>
        </div>
      );
    }
    case 'tableReference': {
      let disabledColumn;
      let hiddenColumn;
      if (uiSchema.fieldSettings != null) {
        assert(
          isTableReferenceFieldSettings(uiSchema.fieldSettings),
          'tableReference fieldSettings must be type TableReferenceFieldSettings'
        );
        disabledColumn = uiSchema.fieldSettings.disabledColumn;
        hiddenColumn = uiSchema.fieldSettings.hiddenColumn;
      }
      return (
        <TableReferenceEditor
          config={
            maybeGetValueFromConfig(config, uiSchema.configShapePath) as Array<Record<string, JSONValue>>
          }
          configShape={currentConfigShape}
          onChangeConfig={(newValue) => {
            onChangeConfig(config, uiSchema.configShapePath, newValue);
          }}
          dataTestAttrs={{component: 'StepConfigEditor', element: 'table', ...dataTestAttrs}}
          disabled={shouldDisableEditor}
          disabledColumns={disabledColumn}
          hiddenColumns={hiddenColumn}
        />
      );
    }
    case 'domain': {
      const domainValue = maybeGetValueFromConfig(config, uiSchema.configShapePath) as string | null;
      const onChangeDomainValue = (newValue: string | null): void => {
        onChangeConfig(config, uiSchema.configShapePath, newValue);
      };
      // Folders have no SmartChip type, so they can't render through the search picker; entity schemas
      // have a dedicated product selector. Both use the standard picker rather than a search chip.
      const isFolder = currentConfigShape.types.length === 1 && currentConfigShape.types[0] === 'FOLDER';
      const isEntitySchema =
        currentConfigShape.types.length === 1 && currentConfigShape.types[0] === 'ENTITY_SCHEMA';
      const isLocation = currentConfigShape.types.length === 1 && currentConfigShape.types[0] === 'LOCATION';
      const isPlateDesignTemplate =
        currentConfigShape.types.length === 1 && currentConfigShape.types[0] === 'PLATE_DESIGN_TEMPLATE';
      // A step may route only a subset of the picker's types (e.g. the entity steps support a fixed set of
      // entity subtypes); the backend stamps that allowlist onto the shape as routableTypes. Restrict the
      // picker to those; absent means no narrowing. Keeps the allowlist server-side, not duplicated here.
      // The routable object types all map to OligoAwareFolderItemType members (a subset of ModelTypeConstants),
      // so the cast is safe; SchemaEditor's picker filters on that narrower type.
      const entitySchemaTypeFilter =
        currentConfigShape.routableTypes != null
          ? currentConfigShape.routableTypes.map(
              (type) => getModelTypeConstantForObjectType(type) as OligoAwareFolderItemType
            )
          : null;
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            {isFolder ? (
              <FolderEditor
                value={domainValue}
                onChange={onChangeDomainValue}
                required={Boolean(currentConfigShape.required)}
                disabled={shouldDisableEditor}
                showRegistries={
                  isFolderFieldSettings(uiSchema.fieldSettings) ? uiSchema.fieldSettings.showRegistries : true
                }
              />
            ) : isLocation ? (
              <LocationEditor
                value={domainValue}
                onChange={onChangeDomainValue}
                required={Boolean(currentConfigShape.required)}
                disabled={shouldDisableEditor}
              />
            ) : isEntitySchema ? (
              <SchemaEditor
                value={domainValue}
                onChange={onChangeDomainValue}
                required={Boolean(currentConfigShape.required)}
                disabled={shouldDisableEditor}
                oligoAwareFolderItemTypes={entitySchemaTypeFilter}
                implementsSchemaInterfaceId={
                  isSchemaFilterFieldSettings(uiSchema.fieldSettings)
                    ? uiSchema.fieldSettings.implementsSchemaInterfaceId
                    : undefined
                }
              />
            ) : isPlateDesignTemplate ? (
              <PlateDesignTemplateSearchEditor
                value={domainValue}
                onChange={onChangeDomainValue}
                disabled={shouldDisableEditor}
              />
            ) : (
              <SearchEditor
                config={domainValue}
                onChangeConfig={onChangeDomainValue}
                disabled={shouldDisableEditor}
                modelTypes={currentConfigShape.types.map((type) => getModelTypeConstantForObjectType(type))}
                schemaIds={currentConfigShape.schemaIds}
                schemaInterfaceIds={currentConfigShape.schemaInterfaceIds}
              />
            )}
          </div>
        </div>
      );
    }
    case 'array': {
      if (uiSchema.editorOverride === StepConfigEditorOverride.MAPPING_ARRAY) {
        assert(
          isMappingArrayFieldSettings(uiSchema.fieldSettings),
          'MAPPING_ARRAY editorOverride requires MappingArrayFieldSettings'
        );
        const mappingSettings = uiSchema.fieldSettings;
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <QueryingMappingArrayEditor
                config={config}
                configShape={currentConfigShape}
                configShapePath={uiSchema.configShapePath}
                onChangeConfig={onChangeConfig}
                mappingSettings={mappingSettings}
                elementUISchema={uiSchema.elementUISchema}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      }

      if (currentConfigShape.elementSpec.kind === 'domain') {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <MultiSelectSearchEditor
                config={(maybeGetValueFromConfig(config, uiSchema.configShapePath) as Array<string>) ?? []}
                onChangeConfig={(newValue) => {
                  onChangeConfig(config, uiSchema.configShapePath, newValue);
                }}
                disabled={shouldDisableEditor}
                maxItems={currentConfigShape.maxItems}
                modelTypes={currentConfigShape.elementSpec.types.map((type) =>
                  getModelTypeConstantForObjectType(type)
                )}
                schemaIds={currentConfigShape.elementSpec.schemaIds}
                schemaInterfaceIds={currentConfigShape.elementSpec.schemaInterfaceIds}
              />
            </div>
          </div>
        );
      }

      if (currentConfigShape.elementSpec.kind === 'columnReference') {
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <MultiSelectColumnReferenceEditor
                config={
                  (maybeGetValueFromConfig(config, uiSchema.configShapePath) as Array<string | null>) ?? []
                }
                configShape={currentConfigShape}
                dataTestAttrs={{
                  component: 'StepConfigEditor',
                  element: 'multiSelectDropdown',
                  ...dataTestAttrs,
                }}
                onChangeConfig={(newValue) => {
                  onChangeConfig(config, uiSchema.configShapePath, newValue);
                }}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      }

      let disableAddRemove;
      let hideDefaultAddButton;
      if (uiSchema.fieldSettings != null) {
        assert(
          isArrayFieldSettings(uiSchema.fieldSettings),
          'array fieldSettings must be type ArrayFieldSettings'
        );
        disableAddRemove = uiSchema.fieldSettings.disableAddRemove;
        hideDefaultAddButton = uiSchema.fieldSettings.hideDefaultAddButton;
      }
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <ArrayEditor
              config={config}
              configShape={currentConfigShape}
              dataTestAttrs={dataTestAttrs}
              onChangeConfig={onChangeConfig}
              elementUISchema={uiSchema.elementUISchema}
              disabled={shouldDisableEditor}
              configShapePath={uiSchema.configShapePath}
              disableAddRemove={disableAddRemove}
              hideDefaultAddButton={hideDefaultAddButton}
            />
          </div>
        </div>
      );
    }
    case 'columnMapping': {
      let columnSourceInputDataName: string | undefined;
      let subfieldDisplayNames: Record<string, string> | undefined;
      let subfieldInfoText: Record<string, string> | undefined;
      if (uiSchema.fieldSettings != null) {
        assert(
          isColumnMappingFieldSettings(uiSchema.fieldSettings),
          'columnMapping fieldSettings must be type ColumnMappingFieldSettings'
        );
        columnSourceInputDataName = uiSchema.fieldSettings.columnSourceInputDataName;
        subfieldDisplayNames = uiSchema.fieldSettings.subfieldDisplayNames;
        subfieldInfoText = uiSchema.fieldSettings.subfieldInfoText;
      }
      return (
        <div className={css(styles.oneRowWrapper, styles.grow)}>
          <ColumnMappingEditor
            config={config}
            configShape={currentConfigShape}
            configShapePath={uiSchema.configShapePath}
            onChangeConfig={onChangeConfig}
            disabled={shouldDisableEditor}
            columnSourceInputDataName={columnSourceInputDataName}
            subfieldDisplayNames={subfieldDisplayNames}
            subfieldInfoText={subfieldInfoText}
          />
        </div>
      );
    }
    case 'shape': {
      if (uiSchema.editorOverride === StepConfigEditorOverride.DATE_RANGE) {
        const configValue = maybeGetValueFromConfig(config, uiSchema.configShapePath) as JSONObject | null;
        assert(
          uiSchema.fieldSettings != null && isRangeFieldSettings(uiSchema.fieldSettings),
          'Range fieldSettings is required for range picker editor override'
        );
        return (
          <div className={css(styles.oneRowWrapper)}>
            <div className={css(styles.grow)}>
              <DateRangeEditor
                config={configValue ?? {}}
                configShape={currentConfigShape}
                onChangeConfig={(newValue) => {
                  onChangeConfig(config, uiSchema.configShapePath, newValue);
                }}
                fieldSettings={uiSchema.fieldSettings}
                dataTestAttrs={{
                  component: 'StepConfigEditor',
                  element: 'dateRangePicker',
                  ...dataTestAttrs,
                }}
                disabled={shouldDisableEditor}
              />
            </div>
          </div>
        );
      }

      // Auto-generate UISchema for shapes that don't have one.
      // This commonly happens when ArrayEditor wraps scalar elements
      // in temporary shapes, but also serves as a fallback for any shape
      // rendered without explicit UI configuration.
      const fields = Object.keys(currentConfigShape.fields);
      const shapeUISchema: ShapeUISchema = {
        [FIELD_ORDER_UI_SCHEMA_KEY]: fields,
        ...Object.fromEntries(
          fields.map((fieldName) => [
            fieldName,
            {
              displayName: fieldName,
              configShapePath: [...uiSchema.configShapePath, fieldName],
            },
          ])
        ),
      };

      return (
        <ShapeEditor
          configShape={currentConfigShape}
          config={config}
          onChangeConfig={onChangeConfig}
          uiSchema={shapeUISchema}
          disabled={disabled}
        />
      );
    }
    default:
      assertAndContinue(false, 'Unsupported config shape', {kind: currentConfigShape.kind});
      return <div>Unsupported</div>;
  }
}

/**
 * Render the correct editor based on the `uiSchema` and `configShape`.
 *
 * The `uiSchema` is the structure of the editor, and the `configShape`. The router recursively renders
 * editors for each field in the `uiSchema`, rendering the correct editor based on its associated field
 * in configShape.
 *
 */
export default function StepConfigEditorRouter({
  config,
  onChangeConfig,
  configShape,
  uiSchema,
  disabled,
  dataTestAttrs = {},
  isExpandable,
  isExpanded,
  arrayItemControls,
}: StepConfigEditorRouterProps): JSX.Element {
  const {dataFramesInfos, actions} = useStepConfigEditorContext();
  if (isShapeUISchema(uiSchema)) {
    if (uiSchema.hasOwnProperty(CONDITIONAL_UI_SCHEMA_KEY) && uiSchema[CONDITIONAL_UI_SCHEMA_KEY] != null) {
      const columns = getColumnsFromDataFramesInfos(dataFramesInfos);
      if (!doesConditionMatch(config, uiSchema[CONDITIONAL_UI_SCHEMA_KEY], columns)) {
        return <></>;
      }
    }
    return (
      <ShapeEditor
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        uiSchema={uiSchema}
        disabled={disabled}
        arrayItemControls={arrayItemControls}
      />
    );
  }

  if (isFieldUISchemaWithCustomComponent(uiSchema)) {
    const CustomComponent = uiSchema[CUSTOM_COMPONENT_KEY];
    return (
      <CustomComponent
        configShape={configShape}
        config={config}
        onChangeConfig={onChangeConfig}
        disabled={disabled}
      />
    );
  }

  if (isFieldUISchemaActionButton(uiSchema)) {
    const action = actions?.[uiSchema.key];
    return (
      <div
        className={css(styles.actionButtonWrapper)}
        {...getTestProps({
          component: 'StepConfigEditor',
          element: 'button',
          key: uiSchema.key,
          ...dataTestAttrs,
        })}
        data-amp-safe={false}
      >
        <Button
          leftIcon={
            uiSchema.buttonIcon != null && isValidBenchlingIcon(uiSchema.buttonIcon) ? (
              <BenchlingIcon icon={uiSchema.buttonIcon} />
            ) : null
          }
          onClick={action != null ? action : noop}
          variant={uiSchema.buttonVariant}
          disabled={action == null}
        >
          {uiSchema.buttonText}
        </Button>
        {uiSchema.helpText}
      </div>
    );
  }

  if (isFieldUISchemaMarkdown(uiSchema)) {
    return (
      <div className={css(styles.oneRowWrapper)}>
        <MarkdownContent content={uiSchema.markdown} />
      </div>
    );
  }

  return (
    <FieldEditor
      dataFramesInfos={dataFramesInfos}
      configShape={configShape}
      config={config}
      onChangeConfig={onChangeConfig}
      uiSchema={uiSchema}
      dataTestAttrs={dataTestAttrs}
      disabled={disabled}
      isExpandable={isExpandable}
      isExpanded={isExpanded}
    />
  );
}

const styles = StyleSheet.create({
  textArea: {
    resize: 'vertical',
  },
  grow: {
    flexGrow: 1,
  },
  oneRowWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  primitiveEditor: {
    ...FONT_PROXIMA_13,
    borderRadius: 4,
    height: 36,
  },
  actionButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  colorInput: {
    width: '100%',
    height: 36,
  },
  stringLabelWrapper: {
    marginBottom: '-8px',
  },
});
